import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Container,
  Grid,
  Tabs,
  Tab,
  Paper,
  Fab,
  Zoom,
} from "@mui/material";
import { format } from "date-fns";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

// Hooks
import { useMatches } from "./hooks/useMatches";

// Componentes
import LeaguesList from "./components/LeaguesList";
import MatchesList from "./components/MatchesList";
import MatchDetails from "./components/MatchDetails";
import DateNavigation from "./components/DateNavigation";

export default function DashboardV2() {
  // Usar o hook de partidas
  const {
    state,
    filteredMatches,
    groupedMatches,
    handleTabChange,
    changeDate,
    handleSearch,
    toggleFavorite,
    selectMatch,
    isMobile,
    openDetailsMobile,
    closeMobileDetails,
    selectSpecificDate,
  } = useMatches();

  // Estado para controlar a visibilidade do botão "voltar ao topo"
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Função para verificar a posição de scroll e mostrar/esconder o botão
  useEffect(() => {
    const checkScrollPosition = () => {
     if(window.scrollY > 20){
      setShowScrollTop(true);
      return;
     }

     if(window.scrollY < 20){
      setShowScrollTop(false);
     }
    };

    // Adicionar event listener
    window.addEventListener("scroll", checkScrollPosition);
    
    // Verificar posição inicial
    checkScrollPosition();
    
    // Limpar event listener ao desmontar
    return () => window.removeEventListener("scroll", checkScrollPosition);
  }, []);

  // Função para voltar ao topo
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // Extrair valores do estado
  const {
    date,
    loading,
    tabValue,
    leagues,
    liveMatches,
    selectedMatch,
    favorites,
    searchQuery,
  } = state;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Cabeçalho */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" component="h1" fontWeight="bold">
          Futebol
        </Typography>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" component="span" sx={{ mr: 1 }}>
            {format(date, "dd/MM/yyyy")}
          </Typography>
        </Box>
      </Box>

      {/* Barra de navegação de datas e pesquisa */}
      <DateNavigation
        date={date}
        searchQuery={searchQuery}
        onDateChange={changeDate}
        onSearchChange={handleSearch}
        onSpecificDateChange={selectSpecificDate}
      />

      {/* Tabs de filtro */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="match filter tabs"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Todos" />
          <Tab label={`Ao Vivo (${liveMatches.length})`} />
          <Tab label="Finalizados" />
          <Tab label="Favoritos" />
        </Tabs>
      </Box>

      {/* Botão para voltar ao topo */}
      <Zoom in={showScrollTop}>
        <Fab 
          color="primary" 
          size="medium" 
          aria-label="voltar ao topo"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000,
            width: 48,
            height: 48,
            minHeight: '48px',
            minWidth: '48px',
            boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)'
          }}
        >
          <KeyboardArrowUpIcon fontSize="medium" />
        </Fab>
      </Zoom>

      {/* Layout de 3 colunas */}
      <Grid container spacing={3}>
        {/* Coluna Esquerda - Ligas e Classificações */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: { xs: isMobile ? "none" : "block", md: "block" } }}
        >
          <Paper
            sx={{
              p: 2,
              height: "100%",
            }}
          >
            <LeaguesList
              leagues={leagues}
              favorites={favorites}
              toggleFavorite={toggleFavorite}
            />
          </Paper>
        </Grid>

        {/* Coluna Central - Lista de Jogos */}
        <Grid
          item
          xs={12}
          md={isMobile ? (selectedMatch && openDetailsMobile ? 0 : 12) : 5}
        >
          <Paper
            sx={{
              height: !isMobile ? "600px" : "100%",
              display: "flex",
              flexDirection: "column",
              overflow: !isMobile ? "hidden" : "visible",
              p: 2,
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                overflow: !isMobile ? "auto" : "visible",
                width: "100%",
                height: !isMobile ? "100%" : "auto",
                ".MuiPaper-root": {
                  height: "100%",
                },
              }}
            >
              <MatchesList
                matches={filteredMatches}
                loading={loading}
                selectedMatch={selectedMatch}
                favorites={favorites}
                groupedMatches={groupedMatches}
                onSelectMatch={selectMatch}
                toggleFavorite={toggleFavorite}
                isMobile={isMobile}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Coluna Direita - Detalhes da Partida (versão desktop) */}
        <Grid
          item
          xs={12}
          md={isMobile ? 0 : 4}
          sx={{
            display: isMobile ? "none" : selectedMatch ? "block" : "none",
            height: "auto",
          }}
        >
          <Paper
            sx={{
              height: "600px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
              }}
            >
              <MatchDetails
                match={selectedMatch}
                onClose={closeMobileDetails}
                isMobile={isMobile}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Versão mobile para detalhes da partida com animação */}
        {isMobile && selectedMatch && (
          <Box
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 1500,
              visibility: openDetailsMobile ? "visible" : "hidden",
              transform: openDetailsMobile
                ? "translateY(0)"
                : "translateY(100%)",
              transition:
                "transform 0.3s ease-in-out, visibility 0s linear" +
                (openDetailsMobile ? "" : " 0.3s"),
              bgcolor: "background.paper",
              boxShadow: 3,
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                overflow: "auto",
                p: 2,
              }}
            >
              <MatchDetails
                match={selectedMatch}
                onClose={closeMobileDetails}
                isMobile={isMobile}
              />
            </Box>
          </Box>
        )}
      </Grid>
    </Container>
  );
}
